import { installConsoleCapture } from "./console-tail";
import { setAppName, setQaContextPath } from "./environment";
import * as api from "./api";
import {
  currentRouteKey,
  mountPanel,
  refresh,
  setRoute,
  togglePanel,
} from "./panel";

/**
 * qadeck widget entry point.
 *
 * Bundled by `tools/build-widget.mjs` (esbuild → one IIFE) to
 * `public/qa-widget.js`, which a customer injects with:
 *
 *   <script src="https://qadeck.example/qa-widget.js"
 *           data-qa-deck-url="https://qadeck.example"
 *           data-qa-deck-app="acme-shop" async></script>
 *
 * Guard rails, all inherited from qa-deck and all load-bearing when the script
 * runs on someone else's page:
 *  - **inert when unconfigured** — no `data-qa-deck-url` ⇒ `boot()` returns
 *    before touching the page. That attribute is the only switch;
 *  - idempotent via `window.__qaDeck`;
 *  - never intercepts page events outside pick mode;
 *  - takes no global keyboard shortcut.
 */

interface QaDeckGlobal {
  version: string;
  /** Deliberate test seam: the shadow root is closed to the HOST PAGE, but an
   *  e2e spec needs a way in. Nothing on the page reads this. */
  shadowRoot: ShadowRoot;
  toggle: (open?: boolean) => void;
  refresh: () => Promise<void>;
}

declare global {
  interface Window {
    __qaDeck?: QaDeckGlobal;
  }
}

function ownScript(): HTMLScriptElement | null {
  const current = document.currentScript;
  if (
    current instanceof HTMLScriptElement &&
    current.hasAttribute("data-qa-deck-url")
  ) {
    return current;
  }
  return document.querySelector<HTMLScriptElement>("script[data-qa-deck-url]");
}

function boot(): void {
  if (window.__qaDeck) return;

  const script = ownScript();
  const baseUrl = script?.getAttribute("data-qa-deck-url")?.trim();
  if (!baseUrl) return; // no config ⇒ no widget, no side effects

  api.setBaseUrl(baseUrl);
  setAppName(script?.getAttribute("data-qa-deck-app")?.trim() || "app");

  // Optional: the customer's own tier/flag endpoint. `data-qa-deck-context=""`
  // switches the probe off entirely for sites that would rather we didn't.
  const contextAttr = script?.getAttribute("data-qa-deck-context");
  if (contextAttr !== null && contextAttr !== undefined) {
    setQaContextPath(contextAttr.trim() || null);
  }

  installConsoleCapture();

  const shadowRoot = mountPanel();
  setRoute(currentRouteKey());

  // SPA navigation: most hosts are client-routed, so route changes go through
  // pushState/replaceState rather than a document load.
  const syncRoute = () => setRoute(currentRouteKey());
  for (const name of ["pushState", "replaceState"] as const) {
    const original = history[name].bind(history);
    history[name] = (...args: Parameters<History["pushState"]>) => {
      const result = original(...args);
      syncRoute();
      return result;
    };
  }
  window.addEventListener("popstate", syncRoute);
  window.addEventListener("hashchange", syncRoute);

  // Flush the offline outbox when connectivity returns.
  window.addEventListener("online", () => void refresh());

  window.__qaDeck = {
    version: "1.0.0",
    shadowRoot,
    toggle: togglePanel,
    refresh,
  };

  if (api.getToken()) void api.flushOutbox();
}

boot();
